import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import api from '../../api/axiosInstance.js'
import Swal from 'sweetalert2'
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  Stack,
} from '@mui/material'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Mail,
  CheckCircle,
  XCircle,
  ArrowRight,
  RefreshCw,
  Shield,
  Zap,
} from 'lucide-react'

const ModernVerifyEmailPage = () => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const token = queryParams.get('token')
  const navigate = useNavigate()
  const theme = useTheme()

  const [tokenStatus, setTokenStatus] = useState(null) // null = loading, true = success, false = error
  const [isVerifying, setIsVerifying] = useState(true)

  const checkToken = async () => {
    try {
      if (!token) {
        setTokenStatus(false)
        setIsVerifying(false)
        return
      }

      // Decode token to check validity
      const decodedToken = jwtDecode(token)

      // Verify email with API
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}auth/verify-email?token=${token}`
      )

      // Success
      setTokenStatus(true)
      setIsVerifying(false)
    } catch (error) {
      // Error
      setTokenStatus(false)
      setIsVerifying(false)
    }
  }

  useEffect(() => {
    checkToken()
  }, [])

  const handleResendEmail = async () => {
    try {
      // Add resend email logic here
      Swal.fire({
        title: 'E-posta Gönderildi',
        text: 'Yeni doğrulama e-postası gönderildi. Lütfen gelen kutunuzu kontrol edin.',
        icon: 'success',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
    } catch (error) {
      Swal.fire({
        title: 'Hata',
        text: 'E-posta gönderilemedi. Lütfen tekrar deneyin.',
        icon: 'error',
        confirmButtonText: 'Tamam',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      })
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: theme.palette.background.default,
      }}
    >
      {/* Animated Background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
        }}
      >
        {/* Gradient Orbs */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3],
              x: [0, 50, 0],
              y: [0, -30, 0],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 2,
            }}
            sx={{
              position: 'absolute',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(
                tokenStatus === true
                  ? theme.palette.success.main
                  : tokenStatus === false
                  ? theme.palette.error.main
                  : theme.palette.primary.main,
                0.15
              )}, transparent)`,
              filter: 'blur(60px)',
              top: `${20 + i * 30}%`,
              left: `${10 + i * 30}%`,
            }}
          />
        ))}

        {/* Grid Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px),
              linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            opacity: 0.3,
          }}
        />

        {/* Floating Mail Icons */}
        {[...Array(5)].map((_, i) => (
          <Box
            key={i}
            component={motion.div}
            animate={{
              y: [0, -20, 0],
              opacity: [0.1, 0.3, 0.1],
              rotate: [0, 10, 0],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 1.5,
            }}
            sx={{
              position: 'absolute',
              top: `${10 + i * 20}%`,
              left: `${5 + i * 20}%`,
              opacity: 0.1,
            }}
          >
            <Mail size={40} color={theme.palette.primary.main} />
          </Box>
        ))}
      </Box>

      {/* Content */}
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
            backdropFilter: 'blur(20px)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.1)}`,
            textAlign: 'center',
          }}
        >
          <AnimatePresence mode="wait">
            {/* Loading State */}
            {isVerifying && (
              <Box
                component={motion.div}
                key="loading"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                {/* Loading Icon */}
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 3,
                    borderRadius: '50%',
                    background: alpha(theme.palette.primary.main, 0.1),
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    mb: 3,
                  }}
                >
                  <CircularProgress
                    size={60}
                    thickness={4}
                    sx={{
                      color: theme.palette.primary.main,
                    }}
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2,
                  }}
                >
                  E-posta Doğrulanıyor
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: alpha('#fff', 0.7),
                    lineHeight: 1.6,
                  }}
                >
                  Lütfen bekleyin, e-posta adresiniz doğrulanıyor...
                </Typography>
              </Box>
            )}

            {/* Success State */}
            {!isVerifying && tokenStatus === true && (
              <Box
                component={motion.div}
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                {/* Success Icon */}
                <Box
                  component={motion.div}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2,
                  }}
                  sx={{
                    display: 'inline-flex',
                    p: 3,
                    borderRadius: '50%',
                    background: alpha(theme.palette.success.main, 0.1),
                    border: `2px solid ${alpha(theme.palette.success.main, 0.3)}`,
                    mb: 3,
                  }}
                >
                  <CheckCircle size={60} color={theme.palette.success.main} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: theme.palette.success.main,
                    mb: 2,
                  }}
                >
                  E-posta Doğrulandı!
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: alpha('#fff', 0.7),
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  Tebrikler! E-posta adresiniz başarıyla doğrulandı. Artık tüm
                  özelliklere erişebilirsiniz.
                </Typography>

                {/* Success Features */}
                <Stack spacing={2} sx={{ mb: 4 }}>
                  {[
                    { icon: Shield, text: 'Hesabınız güvende' },
                    { icon: Zap, text: 'Tüm özellikler aktif' },
                    { icon: Mail, text: 'Bildirimler açık' },
                  ].map((item, idx) => {
                    const Icon = item.icon
                    return (
                      <Box
                        key={idx}
                        component={motion.div}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.3 + idx * 0.1 }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          p: 2,
                          borderRadius: 2,
                          background: alpha(theme.palette.success.main, 0.05),
                          border: `1px solid ${alpha(
                            theme.palette.success.main,
                            0.2
                          )}`,
                        }}
                      >
                        <Box
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            background: alpha(theme.palette.success.main, 0.1),
                          }}
                        >
                          <Icon size={20} color={theme.palette.success.main} />
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: alpha('#fff', 0.9),
                            fontWeight: 600,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    )
                  })}
                </Stack>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/user/monitors')}
                  endIcon={<ArrowRight size={20} />}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.light})`,
                    boxShadow: `0 8px 24px ${alpha(
                      theme.palette.success.main,
                      0.3
                    )}`,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: '#000',
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      transform: 'translateY(-2px)',
                      boxShadow: `0 12px 32px ${alpha(
                        theme.palette.success.main,
                        0.4
                      )}`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Dashboard'a Git
                </Button>
              </Box>
            )}

            {/* Error State */}
            {!isVerifying && tokenStatus === false && (
              <Box
                component={motion.div}
                key="error"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.5 }}
              >
                {/* Error Icon */}
                <Box
                  component={motion.div}
                  initial={{ scale: 0, rotate: 180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 15,
                    delay: 0.2,
                  }}
                  sx={{
                    display: 'inline-flex',
                    p: 3,
                    borderRadius: '50%',
                    background: alpha(theme.palette.error.main, 0.1),
                    border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    mb: 3,
                  }}
                >
                  <XCircle size={60} color={theme.palette.error.main} />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: theme.palette.error.main,
                    mb: 2,
                  }}
                >
                  Doğrulama Başarısız
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: alpha('#fff', 0.7),
                    mb: 4,
                    lineHeight: 1.6,
                  }}
                >
                  Bu doğrulama bağlantısı geçersiz veya süresi dolmuş. Lütfen yeni
                  bir doğrulama e-postası isteyin.
                </Typography>

                {/* Error Reasons */}
                <Box
                  sx={{
                    p: 2.5,
                    mb: 4,
                    borderRadius: 2,
                    background: alpha(theme.palette.error.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    textAlign: 'left',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha('#fff', 0.8),
                      fontWeight: 600,
                      mb: 1.5,
                    }}
                  >
                    Olası Nedenler:
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      'Bağlantı süresi dolmuş olabilir',
                      'E-posta zaten doğrulanmış olabilir',
                      'Bağlantı hatalı kopyalanmış olabilir',
                    ].map((reason, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: theme.palette.error.main,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: alpha('#fff', 0.7),
                          }}
                        >
                          {reason}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleResendEmail}
                    startIcon={<RefreshCw size={20} />}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                      boxShadow: `0 8px 24px ${alpha(
                        theme.palette.primary.main,
                        0.3
                      )}`,
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 700,
                      color: '#000',
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 12px 32px ${alpha(
                          theme.palette.primary.main,
                          0.4
                        )}`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Yeni Doğrulama E-postası Gönder
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate('/login')}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: theme.palette.primary.main,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        background: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                  >
                    Giriş Sayfasına Dön
                  </Button>
                </Stack>
              </Box>
            )}
          </AnimatePresence>
        </Paper>

        {/* Help Text */}
        {!isVerifying && (
          <Box
            sx={{
              mt: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: alpha('#fff', 0.5),
              }}
            >
              Sorun mu yaşıyorsunuz?{' '}
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
                onClick={() => navigate('/contact')}
              >
                Destek ekibimize ulaşın
              </Typography>
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default ModernVerifyEmailPage